// PRD 05 §3.9 — editable score-settings section in the slide-panel.
// Toggle disables the 4 inputs without clearing them (values preserved).
//
// SHCT-603 — the auto-derived 정규화값 / 최종값 previews and the formula help box
// are removed (ADM.040 "네, 제거해 주십시오."; PRD 05 §3.9). With them went the
// `previewQuestion` prop — nothing in this section computes a derived value now.

import { useTranslation } from 'react-i18next';
import { Calculator } from 'lucide-react';

import { toast } from '@/components/feedback/useToast';
import { NumberInput } from '@/components/forms/NumberInput';

import { isReliabilityValid } from '../utils/question-validation';
import type { ScoringAdvanced } from '../types/questionnaire-design.types';

interface ScoringSettingsSectionProps {
  advanced: ScoringAdvanced;
  setAdvanced: (next: ScoringAdvanced) => void;
  // §7.1 — weight range/step error, rendered inline under the weight input
  // (SHCT-421 #1). Null when valid or before the first save attempt.
  weightError?: string | null;
}

export function ScoringSettingsSection({
  advanced,
  setAdvanced,
  weightError,
}: ScoringSettingsSectionProps) {
  const { t } = useTranslation('questionnaire-design');
  const disabled = !advanced.scoreContribution;

  // PRD 05 §8.1 — an out-of-range / off-step reliability shows a toast error and
  // restores the previous value (non-blocking; it never blocks Save).
  const onChangeReliability = (next: number) => {
    if (isReliabilityValid(next)) {
      setAdvanced({ ...advanced, reliability: next });
    } else {
      // Do not propagate → the controlled input snaps back to the last valid
      // value ("이전 값 복원"), and surface the message as a toast.
      toast.error(t('validation.reliabilityOutOfRange'));
    }
  };

  return (
    <>
      <div
        style={{
          borderTop: '1px dashed var(--border)',
          margin: 'var(--space-4) 0 var(--space-3)',
        }}
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-3)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <Calculator size={14} color="var(--muted-foreground)" />
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semibold)',
            }}
          >
            {t('scoringBox.title')}
          </span>
        </div>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            fontSize: 'var(--font-size-sm)',
          }}
          title={t('scoringBox.toggleTooltip')}
        >
          <input
            type="checkbox"
            checked={advanced.scoreContribution}
            onChange={(e) =>
              setAdvanced({ ...advanced, scoreContribution: e.target.checked })
            }
            style={{ width: 16, height: 16, accentColor: 'var(--info)' }}
          />
          {t('scoringBox.toggleLabel')}
        </label>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--space-3) var(--space-4)',
          marginBottom: 'var(--space-4)',
          opacity: disabled ? 0.55 : 1,
        }}
      >
        <div className="form-field">
          <label className="form-field__label">
            {t('scoringBox.fields.weight')}
          </label>
          <NumberInput
            className={`form-field__input${weightError ? ' form-field__input--error' : ''}`}
            value={advanced.weight}
            step={0.1}
            min={0.1}
            max={10}
            disabled={disabled}
            aria-invalid={weightError ? 'true' : undefined}
            onChange={(weight) => setAdvanced({ ...advanced, weight })}
          />
          {weightError ? <p className="form-field__error">{weightError}</p> : null}
        </div>
        <div className="form-field">
          <label className="form-field__label">
            {t('scoringBox.fields.reliability')}
          </label>
          <NumberInput
            className="form-field__input"
            value={advanced.reliability}
            step={0.1}
            min={0}
            max={1}
            disabled={disabled}
            onChange={onChangeReliability}
          />
        </div>
        {/* SHCT-603 — the derived 정규화값 / 최종값 read-outs are GONE. ADM.040
            (2026-07-14, DONE) answered "네, 제거해 주십시오." and PRD 05 §3.9 records it:
            "정규화값·최종값 derived 표시와 산식 미리보기는 설계 화면에서 제거". They were also
            printed to 4 decimals, which contradicts §3.9.4 / ADM.031 (소수 첫째자리,
            둘째자리에서 반올림). The section now shows only the four editable inputs. */}
        <div className="form-field">
          <label className="form-field__label">
            {t('scoringBox.fields.min')}
          </label>
          <NumberInput
            className="form-field__input"
            value={advanced.min}
            min={0}
            disabled={disabled}
            onChange={(min) => setAdvanced({ ...advanced, min })}
          />
        </div>
        <div className="form-field">
          <label className="form-field__label">
            {t('scoringBox.fields.max')}
          </label>
          {/* SHCT-605 — 최고점 accepts 0. The floor used to be min={1} (plus a
              `|| 1` empty-fallback), which the browser enforced as a native
              constraint on this panel's <form>, so the "값은 1 이상이어야 합니다."
              tooltip blocked Save. Nothing defines that floor: PRD 05 §4.2 marks the
              min/max range "_TBD M-2", ADM.031 defines the max == min case outright
              ("최대 점수와 최소 점수가 같을 경우 nᵢ = 0"), the M-2 xlsx really does carry
              12 questions with 최저점 0 / 최고점 0, and the BE (QuestionService
              .validateAdvanced) never validates min/max at all. It was a
              front-end invention, so it is gone. */}
          <NumberInput
            className="form-field__input"
            value={advanced.max}
            min={0}
            disabled={disabled}
            onChange={(max) => setAdvanced({ ...advanced, max })}
          />
        </div>
      </div>
      {/* SHCT-603 — the formula help box went with the derived read-outs above
          ("산식 미리보기는 설계 화면에서 제거", PRD 05 §3.9 / ADM.040). */}
    </>
  );
}
